import { useMemo } from 'react'
import * as turf from '@turf/turf'
import { GeoAnalysisSectionLand } from '../../GeoAnalysisSection'
import { calculateMBR, calculateShapeMetricsWithMBR } from '../../utils'

export interface GeometryMetrics {
  area: number
  perimeter: number
  shapeFactor: number
  compactness: number
  orientation: string
  regularity: 'Regular' | 'Moderate' | 'Irregular'
  length: number
  width: number
}

/**
 * Hook to calculate all geometry metrics using actual data and Turf.js
 * @param land - Land parcel data
 * @returns Geometry metrics or null if land is not available
 */
export const useGeometryMetrics = (
  land: GeoAnalysisSectionLand | null | undefined
): GeometryMetrics | null => {
  return useMemo(() => {
    if (!land) return null

    // Basic metrics from API
    const area = land.display_area ?? land.official_area ?? land.area ?? 0

    // Calculate perimeter and shape metrics from polygon if available
    let perimeter = 0
    let shapeFactor = 0
    let compactness = 0
    let orientation = 'N/A'
    let bbox = null
    let length = Math.sqrt(area) * 1.2
    let width = Math.sqrt(area) * 0.8

    // Unwrap Feature to raw geometry if needed
    const geoPolygon =
      land.geo_polygon?.type === 'Feature'
        ? land.geo_polygon.geometry
        : land.geo_polygon

    if (geoPolygon && geoPolygon.coordinates) {
      try {
        let polygonToUse = null

        // Check the GeoJSON type
        if (geoPolygon.type === 'MultiPolygon') {
          // MultiPolygon structure: [ [ [ [lon, lat], ... ] ] ]
          // Take the first polygon from the MultiPolygon
          if (
            Array.isArray(geoPolygon.coordinates) &&
            geoPolygon.coordinates.length > 0 &&
            Array.isArray(geoPolygon.coordinates[0]) &&
            geoPolygon.coordinates[0].length > 0
          ) {
            const firstPolygon = geoPolygon.coordinates[0]
            // Validate that the first ring has at least 4 points
            if (Array.isArray(firstPolygon[0]) && firstPolygon[0].length >= 4) {
              polygonToUse = turf.polygon(firstPolygon)
            } else {
              console.warn('MultiPolygon first ring has less than 4 points')
            }
          }
        } else if (geoPolygon.type === 'Polygon' || !geoPolygon.type) {
          // Regular Polygon structure: [ [ [lon, lat], ... ] ]
          const coordinates = geoPolygon.coordinates

          if (Array.isArray(coordinates) && coordinates.length > 0) {
            // Check if it's a proper polygon structure
            if (Array.isArray(coordinates[0]) && coordinates[0].length >= 4) {
              // Check if coordinates[0][0] is a coordinate pair [lon, lat]
              if (
                Array.isArray(coordinates[0][0]) &&
                coordinates[0][0].length === 2
              ) {
                polygonToUse = turf.polygon(coordinates)
              } else if (typeof coordinates[0][0] === 'number') {
                // Simple array of numbers, needs to be wrapped
                polygonToUse = turf.polygon([coordinates])
              }
            }
          }
        }

        // Only proceed with polygon calculations if we have a valid polygon
        if (polygonToUse) {
          // Calculate perimeter in meters
          // Use Turf's polygonToLine to convert polygon to LineString
          // Then calculate the length - turf.length always returns kilometers by default
          const line = turf.polygonToLine(polygonToUse)
          const perimeterKm = turf.length(line) // Returns in kilometers
          perimeter = perimeterKm * 1000 // Convert km to meters

          try {
            // Calculate Minimum Bounding Rectangle (MBR)
            const mbr = calculateMBR(polygonToUse)

            // Get shape metrics using MBR
            const shapeMetrics = calculateShapeMetricsWithMBR(polygonToUse, mbr)

            // Use MBR dimensions (more accurate for irregular shapes)
            length = mbr.length
            width = mbr.width
            orientation = shapeMetrics.orientation

            // Calculate circularity manually to ensure it's not 0
            // Circularity (Polsby-Popper score) - how close to a circle
            const calculatedCircularity =
              (4 * Math.PI * area) / (perimeter * perimeter)

            // Use both circularity and rectangularity for shape assessment
            // How circular the shape is
            shapeFactor = calculatedCircularity || shapeMetrics.circularity
            compactness = shapeMetrics.rectangularity // How well it fills its MBR

            // Store the MBR angle for display
            bbox = {
              angle: mbr.angle,
              rectangularity: shapeMetrics.rectangularity,
              aspectRatio: shapeMetrics.aspectRatio,
            }
          } catch (mbrError) {
            console.warn(
              'Could not calculate MBR, falling back to AABB:',
              mbrError
            )
            // Fallback to axis-aligned bounding box
            bbox = turf.bbox(polygonToUse)
            width = turf.distance([bbox[0], bbox[1]], [bbox[2], bbox[1]], {
              units: 'meters',
            })
            length = turf.distance([bbox[0], bbox[1]], [bbox[0], bbox[3]], {
              units: 'meters',
            })

            // Simple shape factor calculation
            shapeFactor = (4 * Math.PI * area) / (perimeter * perimeter)
            compactness = shapeFactor

            // Simple orientation
            if (width > length * 1.2) {
              orientation = 'East-West'
            } else if (length > width * 1.2) {
              orientation = 'North-South'
            } else {
              orientation = 'Square'
            }
          }
        }
      } catch (e) {
        console.error('Error calculating geometry metrics:', e)
      }
    }

    return {
      // Size metrics
      area,
      perimeter: perimeter || Math.sqrt(area) * 4, // Estimate if not available

      // Shape metrics
      shapeFactor: Math.min(shapeFactor, 1),
      compactness: Math.min(compactness, 1),
      orientation,
      regularity:
        compactness > 0.85 // Using compactness (rectangularity) for better assessment
          ? 'Regular'
          : compactness > 0.7
            ? 'Moderate'
            : 'Irregular',

      // Dimensions (from bbox or estimates)
      length: length,
      width: width,
    }
  }, [land])
}
